import { knoxApi, type ListOAuthProvidersResponse } from '@/lib/knox-api';
import { trackSignupConversion } from '@/lib/gtag';
import { isCN } from '@/lib/debug-mode';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { isWechatBrowser, clearWechatAutoLoginSuppressed } from '@/lib/utils/wechat-browser';
import { isFeishuClient, clearFeishuAutoLoginSuppressed } from '@/lib/utils/feishu-browser';

interface OAuthButtonsProps {
  inviteToken?: string | null;
  joinToken?: string | null;
}

function useProviderLabels() {
  const { t } = useTranslation('knox-aiops');
  return t;
}

/**
 * Whitelist of OAuth providers shown on the login page, by deployment region.
 *
 * - Overseas (isCN=false): Google + GitHub
 * - China   (isCN=true):  DingTalk + WeChat + WeCom + Feishu
 *
 * Other providers returned by /api/v1/auth/providers (e.g. password/LDAP/easyops)
 * are NOT rendered here — they aren't OAuth, or are platform-internal accounts
 * the end-user wouldn't recognize on a customer-facing login page.
 */
const OAUTH_PROVIDERS_OVERSEAS = ['google', 'github'] as const;
const OAUTH_PROVIDERS_CN = ['dingtalk', 'wechat', 'wecom', 'feishu'] as const;

function getProviderWhitelist(): readonly string[] {
  return isCN() ? OAUTH_PROVIDERS_CN : OAUTH_PROVIDERS_OVERSEAS;
}

const providerIcons: Record<string, JSX.Element> = {
  // ===== Overseas =====
  google: (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
      <path d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" fill="#EA4335"/>
      <path d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" fill="#4285F4"/>
      <path d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" fill="#FBBC05"/>
      <path d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" fill="#34A853"/>
    </svg>
  ),
  github: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" fill="#24292F"/>
    </svg>
  ),
  // ===== China =====
  // DingTalk: 钉钉官方 logo（飞翔的燕子，官方品牌色 #007FFF，path 取自 ai-workbench 官方资源）
  dingtalk: (
    <svg viewBox="0 0 20 31" className="h-5 w-5" aria-hidden="true" style={{ transform: 'scale(0.82)' }}>
      <path d="M18.975 14.517C13.963 10.704 8.313 5.654 2.118.24 1.63-.187 1.197-.02.98.548-.416 4.18.936 7.405 3.123 9.265c2.195 1.867 5.459 3.586 7.458 4.498.078.035.01.148-.069.114C6.84 12.283 4.285 11.132.82 8.5c-.373-.284-.75-.174-.796.38-.283 3.472 1.972 6.198 4.489 7.12 1.557.571 3.261.887 4.842 1.075.082.01.064.13-.02.13-2.036.007-4.497-.474-6.626-1.28-.45-.17-.605.183-.535.463.363 1.467 2.205 3.71 5.136 4.18.548.089 1.133.1 1.653.085.124-.003.154.064.114.158 0 0-1.992 3.362-2.079 3.505-.068.112-.026.202.115.202h2.628c.121 0 .196.077.134.179-.062.101-3.509 5.733-3.672 6.002-.142.234.026.413.292.22l11.597-8.365c.144-.104.11-.245-.093-.245h-2.357c-.153 0-.188-.101-.083-.205.105-.103 2.675-2.627 3.587-3.57.952-.986 1.438-2.792-.171-4.016" fill="#007FFF"/>
    </svg>
  ),
  // WeChat: 微信官方 logo（双气泡，官方品牌色 #07C160，path 取自 ai-workbench 官方资源）
  wechat: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path fill="#07C160" d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.045c.133 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89l-.406-.032zm-2.084 3.222c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.857 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
    </svg>
  ),
  // WeCom 企业微信（蓝色对话气泡，官方品牌色 #2EA1F8）
  wecom: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path d="M8.7 2.2C3.9 2.2 0 5.5 0 9.5c0 2.2 1.2 4.2 3 5.6.2.1.3.4.2.6l-.4 1.5c0 .1 0 .2-.1.2 0 .2.1.3.3.3.1 0 .1 0 .2-.1l1.9-1.1c.2-.1.4-.1.7-.1 1 .3 2 .4 2.9.4.3 0 .5 0 .8 0-.8-2.6.2-5 2-6.4 1.7-1.4 3.9-2 5.8-1.9C16.7 5 12.9 2.2 8.7 2.2zM5.8 6c.6 0 1.2.5 1.2 1.2S6.4 8.4 5.8 8.4 4.6 7.9 4.6 7.2 5.2 6 5.8 6zm5.8 0c.6 0 1.2.5 1.2 1.2s-.5 1.2-1.2 1.2-1.2-.5-1.2-1.2.5-1.2 1.2-1.2z" fill="#2EA1F8"/>
      <path d="M24 15.2c0-3.4-3.4-6.2-7.5-6.2s-7.5 2.8-7.5 6.2 3.4 6.2 7.5 6.2c.8 0 1.7-.1 2.4-.3.2-.1.4-.1.6 0l1.7.9.1.1c.2 0 .3-.1.3-.3 0-.1 0-.1-.1-.2l-.3-1.2c-.1-.2 0-.4.2-.5 1.6-1.2 2.6-2.9 2.6-4.7zm-10-1c-.5 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.5 1-1 1zm5 0c-.5 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.5 1-1 1z" fill="#2EA1F8"/>
    </svg>
  ),
  // Feishu: 官方 connector 图标（与 MCP 资源同源）
  feishu: (
    <img src="/icons/connectors/feishu.png" alt="" className="h-5 w-5" />
  ),
  // ===== SSO (overseas) =====
  // NOTE: Microsoft Entra ID SSO 暂未启用（未测试），保留代码，需要时取消注释。
  /*
  microsoft: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path d="M11.4 2H2v9.4h9.4V2z" fill="#F25022"/>
      <path d="M22 2h-9.4v9.4H22V2z" fill="#7FBA00"/>
      <path d="M11.4 12.6H2V22h9.4v-9.4z" fill="#00A4EF"/>
      <path d="M22 12.6h-9.4V22H22v-9.4z" fill="#FFB900"/>
    </svg>
  ),
  */
};

/**
 * provider 到规范显示名（用于 i18n key 与 fallback）的映射。
 * 显式映射而非字符串拼接，避免 GitHub/WeChat/WeCom 这类驼峰命名的大小写错位。
 */
export const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  google: 'Google',
  github: 'GitHub',
  dingtalk: 'DingTalk',
  wechat: 'WeChat',
  wecom: 'WeCom',
  feishu: 'Feishu',
  // microsoft: 'Microsoft',  // SSO 未测试，暂不启用
};

/**
 * 获取 provider 在按钮上显示的文案。
 * 优先级：i18n key (oauth.continueWith{DisplayName}) > display_name > 规范显示名
 */
function getProviderLabel(t: ReturnType<typeof useTranslation>['t'], provider: string, displayName?: string): string {
  const displayNameStandard = PROVIDER_DISPLAY_NAMES[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
  const key = `oauth.continueWith${displayNameStandard}`;
  const i18nValue = t(key);
  // i18n fallback returns the key itself if not found
  if (typeof i18nValue === 'string' && i18nValue !== key) return i18nValue;
  if (displayName) return displayName;
  return displayNameStandard;
}

export function OAuthButtons({ inviteToken, joinToken }: OAuthButtonsProps) {
  const t = useProviderLabels();
  const { data, isLoading } = useQuery<ListOAuthProvidersResponse>({
    queryKey: ['oauthProviders'],
    queryFn: () => knoxApi.listOAuthProviders(),
    staleTime: 5 * 60 * 1000, // 缓存 5 分钟
    retry: false, // 失败不重试，避免登录页卡住
  });

  function handleOAuthClick(provider: string) {
    trackSignupConversion();
    if (inviteToken) localStorage.setItem('pending_invite_token', inviteToken);
    if (joinToken) localStorage.setItem('pending_join_token', joinToken);

    // 飞书客户端内点击飞书登录：用户主动点击 = 清除抑制标记 + 刷新触发 JSAPI 免登。
    // （此时 FeishuAutoLogin 因被抑制未渲染，点击后清抑制并 reload，LoginPage 重新进入免登）
    // 浏览器内点击飞书：走标准 OAuth 扫码（passport.feishu.cn）。
    if (provider === 'feishu' && isFeishuClient()) {
      clearFeishuAutoLoginSuppressed();
      window.location.reload();
      return;
    }

    // 微信登录：根据是否微信内置浏览器决定走 H5 网页授权（wechat_h5）还是 PC 扫码（wechat）。
    // 用户主动点击 = 恢复正常的微信登录语义，清除"退出登录抑制"标记
    // （否则下次进入 /login 仍会被抑制，无法自动登录）。
    let actualProvider = provider;
    if (provider === 'wechat') {
      actualProvider = isWechatBrowser() ? 'wechat_h5' : 'wechat';
      clearWechatAutoLoginSuppressed();
    }

    window.location.href = knoxApi.oauthAuthorizeUrl(actualProvider);
  }

  // Filter: render only providers in the region-specific whitelist
  // AND actually configured in elevoOne (returned by /api/v1/auth/providers).
  // Preserves whitelist order (not backend order) for stable UI.
  const whitelist = getProviderWhitelist();
  const backendProviders = data?.providers ?? [];
  const backendKeys = new Set(backendProviders.map(p => p.provider));
  const visibleProviders = whitelist
    .filter(key => backendKeys.has(key))
    .map(key => backendProviders.find(p => p.provider === key)!);

  // 加载中不渲染按钮（不显示骨架，也不显示旧硬编码数据）
  if (isLoading) return null;

  // 白名单内的 provider 都未配置时，不渲染（让用户走邮箱+邀请码）
  if (visibleProviders.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {visibleProviders.map(p => (
        <button
          key={p.provider}
          type="button"
          onClick={() => handleOAuthClick(p.provider)}
          aria-label={getProviderLabel(t, p.provider, p.display_name)}
          className="flex w-full items-center justify-center gap-3 rounded-xl border border-black/[0.16] bg-surface-bright px-4 py-3 text-sm font-medium text-foreground transition-all duration-200 hover:border-primary/20 hover:bg-primary/[0.02] hover:shadow-sm active:scale-[0.98]"
        >
          {providerIcons[p.provider]}
          {getProviderLabel(t, p.provider, p.display_name)}
        </button>
      ))}
    </div>
  );
}
